import { useRef, useState, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const EDGE_EPS = 3;

function useScrollNavState(trackRef, contentKey) {
  const [state, setState] = useState({
    hasOverflow: false,
    canLeft: false,
    canRight: false,
  });

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth > clientWidth + EDGE_EPS;
    setState({
      hasOverflow,
      canLeft: hasOverflow && scrollLeft > EDGE_EPS,
      canRight:
        hasOverflow && scrollLeft + clientWidth < scrollWidth - EDGE_EPS,
    });
  }, [trackRef]);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);

    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure, trackRef, contentKey]);

  return { ...state, measure };
}

const Root = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(6px, 1.2vw, 14px);
  margin-bottom: 10px;
`;

const ArrowButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(38px, 3vw + 28px, 46px);
  height: clamp(38px, 3vw + 28px, 46px);
  padding: 0;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.soft};
  transition:
    opacity 0.25s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.foreground};
    transform: scale(1.04);
    background: ${({ theme }) => theme.colors.secondary};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.22;
    cursor: default;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.foreground};
    outline-offset: 2px;
  }
`;

const Track = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => $gap};
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 16px;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * Horizontal pills row with chevron scroll (parity with maison/client CategoryHorizontalNav).
 */
export default function CategoryHorizontalNav({
  children,
  gap = 'clamp(12px, 2vw, 24px)',
  ariaLabel,
  contentKey,
}) {
  const trackRef = useRef(null);
  const { hasOverflow, canLeft, canRight, measure } = useScrollNavState(
    trackRef,
    contentKey,
  );

  const scrollByDir = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const delta = Math.max(200, Math.round(el.clientWidth * 0.72));
    el.scrollBy({ left: dir * delta, behavior: 'smooth' });
    window.requestAnimationFrame(() => {
      window.setTimeout(measure, 350);
    });
  };

  return (
    <Root role="region" aria-label={ariaLabel || 'Browse categories'}>
      {hasOverflow ? (
        <ArrowButton
          type="button"
          aria-label="Scroll left"
          disabled={!canLeft}
          onClick={() => scrollByDir(-1)}
        >
          <ChevronLeft size={22} strokeWidth={1.75} aria-hidden />
        </ArrowButton>
      ) : null}
      <Track ref={trackRef} $gap={gap}>
        {children}
      </Track>
      {hasOverflow ? (
        <ArrowButton
          type="button"
          aria-label="Scroll right"
          disabled={!canRight}
          onClick={() => scrollByDir(1)}
        >
          <ChevronRight size={22} strokeWidth={1.75} aria-hidden />
        </ArrowButton>
      ) : null}
    </Root>
  );
}
